import React, { lazy, Suspense, use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm, useStore } from "@tanstack/react-form";
import { Fieldset } from "@headlessui/react";
import { ErrorMessage, Field, FieldGroup, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import Form from "src/components/base/form";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { ApiUser } from "src/api/api_user";
import { toast } from "react-toastify/unstyled";
import { loadPasswordStrengthChecker, MIN_PASSWORD_SCORE } from "src/components/base/pw-strength";

const PasswordStrength = lazy(() => import("src/components/base/pw-strength"));

/**
 * The properties for {@link ChangePasswordDialog}
 */
export type ChangePasswordDialogProps = {
    /** Callback function when the dialog is closed */
    onClose: () => void;
    /** Whether the dialog is open */
    isOpen: boolean;
};

/**
 * Dialog for changing local user password
 */
export default function ChangePasswordDialog(props: ChangePasswordDialogProps) {
    const [t] = useTranslation("authentication-dialog");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            old_password: "",
            showPasswordOld: false,
            new_password: "",
            showPasswordNew: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiUser.credentials.changePassword({
                    new_password: value.new_password,
                    old_password: value.old_password,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            old_password: res.error.wrong_current_password ? t("error.wrong-password") : undefined,
                            new_password: res.error.weak_password ? t("error.stronger-password") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            toast.success(t("toast.success-change-password"));
            props.onClose();
        },
    });

    const showPasswordNew = useStore(form.store, (state) => state.values.showPasswordNew);
    const showPasswordOld = useStore(form.store, (state) => state.values.showPasswordOld);

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    const passwordStrengthChecker = use(loadPasswordStrengthChecker());

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <DialogTitle>{t("heading.change-password")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit} className={"max-w-lg"}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"old_password"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.old-password")}</RequiredLabel>
                                        <div className={"flex gap-3"}>
                                            <Input
                                                autoFocus={true}
                                                required={true}
                                                type={showPasswordOld ? "text" : "password"}
                                                invalid={fieldApi.state.meta.errors.length !== 0}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            />
                                            <form.Field name={"showPasswordOld"}>
                                                {(field) => (
                                                    <Button
                                                        plain={true}
                                                        onClick={() => field.handleChange((prev) => !prev)}
                                                    >
                                                        {field.state.value ? <EyeSlashIcon /> : <EyeIcon />}
                                                    </Button>
                                                )}
                                            </form.Field>
                                        </div>

                                        {fieldApi.state.meta.errors.map((error) => (
                                            <ErrorMessage key={error}>{error}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field
                                name={"new_password"}
                                validators={{
                                    onChange: ({ value }) => {
                                        if (
                                            value !== "" &&
                                            passwordStrengthChecker.check(value).score < MIN_PASSWORD_SCORE
                                        ) {
                                            return t("error.stronger-password");
                                        }
                                        return undefined;
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <Field className={"grid grid-cols-subgrid gap-3"}>
                                        <RequiredLabel>{t("label.new-password")}</RequiredLabel>
                                        <div className={"flex gap-3"}>
                                            <Input
                                                required={true}
                                                type={showPasswordNew ? "text" : "password"}
                                                invalid={fieldApi.state.meta.errors.length !== 0}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            />
                                            <form.Field name={"showPasswordNew"}>
                                                {(field) => (
                                                    <Button
                                                        plain={true}
                                                        onClick={() => field.handleChange((prev) => !prev)}
                                                    >
                                                        {field.state.value ? <EyeSlashIcon /> : <EyeIcon />}
                                                    </Button>
                                                )}
                                            </form.Field>
                                        </div>

                                        {fieldApi.state.meta.errors.map((error) => (
                                            <ErrorMessage key={error}>{error}</ErrorMessage>
                                        ))}

                                        <Suspense fallback={<div></div>}>
                                            <PasswordStrength password={fieldApi.state.value} />
                                        </Suspense>
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <PrimaryButton type="submit"> {tg("button.save")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
